import { ChevronLeft, ChevronRight } from "lucide-react";

import { formatInteger } from "../utils/format";
import { ToolbarSelect } from "./ToolbarSelect";

/** Quantas linhas há, dito de uma das duas formas possíveis.
 *
 * `total` é o caso normal — o servidor devolve envelope paginado e a
 * tela sabe quantas linhas existem no recorte inteiro.
 *
 * `hasNext` é o caso da tela de menções no modo PUBLICAÇÕES: contar
 * publicações custa ~45% do tempo da tela (medido), então o servidor não
 * conta — devolve só se existe próxima página. Sem o total não dá pra
 * dizer "de 900" nem "1 / 36", e o rodapé mostra só o intervalo do que
 * veio.
 *
 * União discriminada, e não dois campos opcionais, porque as duas formas
 * são mutuamente exclusivas: com os dois opcionais, uma tela poderia
 * passar nenhum dos dois e o rodapé apareceria sem saber onde está. */
export type PagerCount =
  | { total: number }
  | { hasNext: boolean; countOnPage: number };

type PagerProps = {
  page: number;
  size: number;
  onPage: (page: number) => void;
  count: PagerCount;
  /** Quando a tela oferece escolher quantas linhas cabem na página. */
  sizes?: readonly number[];
  onSize?: (size: number) => void;
  /** Desabilita os botões enquanto a consulta está em voo — sem isso,
   * clicar duas vezes rápido dispara duas buscas e a segunda resposta
   * pode chegar antes da primeira. */
  busy?: boolean;
};

/** O rodapé de paginação de todas as telas.
 *
 * Existiam QUATRO — menções, relatório social, trilha de auditoria e os
 * catálogos de Ajustes —, cada um com a mesma aritmética escrita de
 * novo: `Math.ceil(total / size)` pro número de páginas,
 * `(page - 1) * size + 1` pro primeiro da página e um `Math.min` pro
 * último. A conta é a mesma; o que variava era a ORDEM dos argumentos
 * do `Math.min` e se o rodapé some quando só há uma página.
 *
 * Divergir aqui não estoura: mostra "26–50 de 40". Foi o suficiente pra
 * unificar.
 *
 * **Some sozinho quando não há o que paginar** — uma página só e sem
 * seletor de tamanho. Num catálogo pequeno o rodapé seria só ruído
 * dizendo "1 de 1". Com seletor ele fica, porque escolher "100 por
 * página" é útil mesmo com 30 linhas. */
export function Pager({
  page,
  size,
  onPage,
  count,
  sizes,
  onSize,
  busy = false
}: PagerProps) {
  const knowsTotal = "total" in count;
  const pages = knowsTotal ? Math.max(1, Math.ceil(count.total / size)) : null;
  const canGoNext = knowsTotal ? page < (pages ?? 1) : count.hasNext;
  const rows = knowsTotal ? count.total : count.countOnPage;

  const showsSize = sizes !== undefined && onSize !== undefined;
  if (pages !== null && pages <= 1 && !showsSize) return null;

  const first = rows === 0 ? 0 : (page - 1) * size + 1;
  const last = knowsTotal
    ? Math.min(page * size, count.total)
    : (page - 1) * size + count.countOnPage;

  return (
    <div className="pager">
      <span className="pager-count">
        {rows === 0
          ? "0"
          : `${formatInteger(first)}–${formatInteger(last)}`}
        {knowsTotal ? ` de ${formatInteger(count.total)}` : ""}
      </span>
      <div className="pager-controls">
        {showsSize && (
          <ToolbarSelect
            label="Linhas por página"
            ariaLabel="Linhas por página"
            align="right"
            direction="up"
            value={String(size)}
            onChange={value => onSize(Number(value))}
            options={sizes.map(option => ({ value: String(option), label: String(option) }))}
          />
        )}
        <button
          type="button"
          className="pager-step"
          disabled={page <= 1 || busy}
          onClick={() => onPage(page - 1)}
          aria-label="Página anterior"
        >
          <ChevronLeft size={16} />
        </button>
        {pages !== null && (
          <span className="pager-page">
            {page} / {pages}
          </span>
        )}
        <button
          type="button"
          className="pager-step"
          disabled={!canGoNext || busy}
          onClick={() => onPage(page + 1)}
          aria-label="Próxima página"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
